import PropTypes from 'prop-types';
import { SimpleForm, TextInput, ReferenceInput, SelectInput, required, maxLength, AutocompleteInput, NumberInput, regex, useTranslate } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { productSources } from '@/components/product/productSources.jsx';
import { decimalLengthValidator, noCharactersFromList, validGtin } from '@/components/global/validators/customValidators.jsx';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { styled } from '@mui/material/styles';
import { TagArrayInput } from '@/components/global/tags/TagArrayInput.jsx';
import { boolChoices, productStatusChoices, productColorChoices } from '@/components/global/options/selectOptions.jsx';
import { useTagNameSource } from '@/i18n/hooks.js';
import { currencyChoices } from '@/components/product/currencyChoices.js';
import { productFormHeadingStyles } from '@/themes/commonStyles/productForm.js';

const ProductFormToolbar = ({ handleClose = () => {}, isEdit = false }) => {
    const translate = useTranslate();
    return (
        <TagCategoryToolbar
            onCancel={handleClose}
            cancelLabel={translate('common.cancel')}
            saveLabel={isEdit ? translate('common.save') : translate('resources.product.labels.addProduct')}
        />
    );
};

ProductFormToolbar.propTypes = {
    handleClose: PropTypes.func,
    isEdit: PropTypes.bool,
};

const StyledProductForm = styled(Box)(productFormHeadingStyles);

export const ProductForm = ({ handleClose, isEdit = false }) => {
    const translate = useTranslate();
    const tagNameSource = useTagNameSource();
    const filterToQuery = searchText => ({
        search: {
            value: `${searchText}`,
            fields: [],
        },
    });

    return (
        <StyledProductForm>
            <SimpleForm
                mode='onBlur'
                reValidateMode='onBlur'
                sx={{ width: 800 }}
                toolbar={isEdit ? <EditToolbar /> : <ProductFormToolbar handleClose={handleClose} isEdit={isEdit} />}
            >
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('resources.product.labels.basicInfo')}
                    </Typography>
                    <TextInput
                        source='ean'
                        label={translate('resources.product.fields.ean')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), validGtin]}
                    />
                    <Box className='autocomplete-parent'>
                        <ReferenceInput source='materialId' reference='material' perPage={25}>
                            <AutocompleteInput
                                filterToQuery={filterToQuery}
                                fullWidth={true}
                                label={translate('resources.product.fields.material')}
                                disablePortal={true}
                                validate={required(translate('common.validation.required'))}
                            />
                        </ReferenceInput>
                    </Box>
                    <TextInput
                        source='name'
                        label={translate('resources.product.fields.productName')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), maxLength(500, translate('common.validation.maxLength', { count: 500 }))]}
                    />
                    <TextInput source='shortenedNameAuto' label={translate('resources.product.fields.shortenedNameAuto')} fullWidth={true} disabled />
                    <TextInput source='shortenedNameManual' label={translate('resources.product.fields.shortenedNameManual')} fullWidth={true} />
                    <TextInput
                        source='brand'
                        label={translate('resources.product.fields.brandProduct')}
                        fullWidth={true}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(255, translate('common.validation.maxLength', { count: 255 })),
                            noCharactersFromList('$^*()/\\"\'`|<>,.[]{}'),
                        ]}
                    />
                    <SelectInput
                        source='status'
                        label={translate('common.status')}
                        validate={required(translate('common.validation.required'))}
                        choices={productStatusChoices(translate)}
                        fullWidth={true}
                        defaultValue={0}
                    />
                    <TextInput
                        source='gln'
                        label={translate('resources.product.fields.gln')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), regex(/^\d{13}$/, translate('common.validation.invalidFormat'))]}
                    />
                    <TextInput
                        source='gpc'
                        label={translate('resources.product.fields.gpc')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), regex(/^\d{8}$/, translate('common.validation.invalidFormat'))]}
                    />
                    <Box className='product-form-box'>
                        <Box flex={1} mr='1em'>
                            <SelectInput
                                source='reusable'
                                label={translate('resources.product.fields.reusable')}
                                fullWidth={true}
                                validate={[required(translate('common.validation.required'))]}
                                choices={boolChoices(translate)}
                                defaultValue={0}
                            />
                        </Box>
                        <Box flex={1}>
                            <SelectInput
                                source='source'
                                label={translate('resources.product.fields.source')}
                                fullWidth={true}
                                validate={[required(translate('common.validation.required'))]}
                                choices={productSources}
                                emptyText={translate('common.all')}
                            />
                        </Box>
                    </Box>
                    <TagArrayInput source='tagIds' label={translate('resources.product.fields.tags')} searchFields={[tagNameSource]} />
                </Box>
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('resources.product.fields.owner')}
                    </Typography>
                    <Box className='product-form-box'>
                        <Box flex={1} mr='1em'>
                            <SelectInput
                                source='ownerContractor'
                                label={translate('resources.product.fields.ownerContractor')}
                                fullWidth={true}
                                validate={[required(translate('common.validation.required'))]}
                                choices={boolChoices(translate)}
                                defaultValue={0}
                            />
                        </Box>
                        <Box flex={2}>
                            <TextInput
                                source='owner'
                                label={translate('resources.product.fields.owner')}
                                fullWidth={true}
                                validate={[maxLength(255, translate('common.validation.maxLength', { count: 255 }))]}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('resources.product.labels.colors')}
                    </Typography>
                    <Box className='product-form-box'>
                        <Box flex={1} mr='1em'>
                            <SelectInput
                                source='materialColor'
                                label={translate('resources.product.fields.materialColor')}
                                fullWidth={true}
                                choices={productColorChoices(translate)}
                                emptyText={translate('common.all')}
                            />
                        </Box>
                        <Box flex={1}>
                            <SelectInput
                                source='capColor'
                                label={translate('resources.product.fields.capColor')}
                                fullWidth={true}
                                choices={productColorChoices(translate)}
                                emptyText={translate('common.all')}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('resources.product.labels.physicalProperties')}
                    </Typography>
                    <NumberInput
                        source='volume'
                        label={translate('resources.product.fields.volume')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                    <NumberInput
                        source='totalWeight'
                        label={translate('resources.product.fields.totalWeight')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                    <NumberInput
                        source='noCupWeight'
                        label={translate('resources.product.fields.noCupWeight')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                    <NumberInput
                        source='height'
                        label={translate('resources.product.fields.height')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                    <NumberInput
                        source='width'
                        label={translate('resources.product.fields.width')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                    <NumberInput
                        source='thickness'
                        label={translate('resources.product.fields.thickness')}
                        fullWidth={true}
                        validate={[required(translate('common.validation.required')), decimalLengthValidator]}
                        defaultValue={0}
                        min={0}
                        max={10000}
                    />
                </Box>
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('resources.product.fields.deposit')}
                    </Typography>
                    <Box className='product-form-box'>
                        <Box flex={2} mr='1em'>
                            <NumberInput
                                source='deposit'
                                label={translate('resources.product.fields.depositValue')}
                                min='0.00'
                                step='0.01'
                                fullWidth={true}
                                validate={required(translate('common.validation.required'))}
                            />
                        </Box>
                        <Box flex={1}>
                            <SelectInput
                                source='currency'
                                label={translate('resources.product.fields.currency')}
                                fullWidth={true}
                                validate={required(translate('common.validation.required'))}
                                choices={currencyChoices}
                                defaultValue='PLN'
                            />
                        </Box>
                    </Box>
                </Box>
                <Box className='product-form-container'>
                    <Typography variant='h5' gutterBottom>
                        {translate('common.operator')}
                    </Typography>
                    <TextInput
                        className='operator-input'
                        source='operatorId'
                        label={translate('resources.product.fields.operatorId')}
                        fullWidth={true}
                        validate={[maxLength(30, translate('common.validation.maxLength', { count: 30 }))]}
                    />
                    <Box className='product-form-box'>
                        <Box flex={1} mr='1em'>
                            <DateInput
                                className='date-input'
                                source='marketIntroductionDate'
                                label={translate('resources.product.fields.marketIntroductionDate')}
                                fullWidth={true}
                            />
                        </Box>
                        <Box flex={1}>
                            <DateInput
                                className='date-input'
                                source='marketWithdrawalDate'
                                label={translate('resources.product.fields.marketWithdrawalDate')}
                                fullWidth={true}
                            />
                        </Box>
                    </Box>
                    <Box className='product-form-box'>
                        <Box flex={1} mr='1em'>
                            <DateInput
                                className='date-input'
                                source='operatorStartDate'
                                label={translate('resources.product.fields.operatorStartDate')}
                                fullWidth={true}
                                placement='topStart'
                            />
                        </Box>
                        <Box flex={1}>
                            <DateInput
                                className='date-input'
                                source='operatorEndDate'
                                label={translate('resources.product.fields.operatorEndDate')}
                                fullWidth={true}
                                placement='topStart'
                            />
                        </Box>
                    </Box>
                </Box>
                <Typography variant='h5' gutterBottom>
                    {translate('resources.product.labels.productPhoto')}
                </Typography>
                <TextInput source='pictureUrl' label={translate('resources.product.fields.pictureUrl')} fullWidth={true} />
            </SimpleForm>
        </StyledProductForm>
    );
};

ProductForm.propTypes = {
    handleClose: PropTypes.func,
    isEdit: PropTypes.bool,
};
